/**
 * Draft Progress Tracker Component
 * Shows current draft progress, team composition, and upcoming packs
 *
 * Composition is read through the shared normalizer (draftComposition.js) so
 * this component, PackRecommendationEngine and SequentialPackSystem all agree
 * on the backend contract. Unknown counts/limits stay null and render as '—'.
 */
import React from 'react';
import './DraftProgressTracker.css';
import { normalizeComposition, getPositionCount, getPositionLimit } from '../utils/draftComposition';

const DraftProgressTracker = ({ teamState, availablePacks, catalog, compact = false }) => {
    if (!teamState) return null;

    const composition = normalizeComposition(teamState, 'DraftProgressTracker');
    // total_packs_completed is guaranteed by the backend draft_state contract —
    // that is the exact field name emitted by TeamDraftStateSerializer.Meta.fields
    // (pack_serializers.py:204-209). There is NO `packs_completed` key; reading
    // that name made this guard fire on every healthy draft and render "—/19".
    // A `|| 0` here would render a missing field as a believable "0/19", which
    // is exactly how the packs_remaining bug shipped unnoticed. Log loudly and
    // degrade visibly instead. A genuine 0 still renders as 0.
    const packsCompleted = (() => {
        if (typeof teamState.total_packs_completed !== 'number') {
            console.error(
                '[DraftProgressTracker] CONTRACT VIOLATION: teamState.total_packs_completed is missing or not a number. ' +
                'The backend guarantees this field on draft_state.',
                {
                    total_packs_completed: teamState.total_packs_completed,
                    total_packs_completed_typeof: typeof teamState.total_packs_completed,
                    teamStateKeys: Object.keys(teamState)
                }
            );
            return null;
        }
        return teamState.total_packs_completed;
    })();
    // Pack/roster totals are backend-driven (league templates vary — e.g.
    // 19-pack SEASON drafts with 2QB/4RB/3WR vs 15-pack daily drafts).
    // Never hardcode counts here.
    const totalPacks = catalog?.total_packs;
    const progressPercent = (totalPacks && packsCompleted !== null)
        ? (packsCompleted / totalPacks) * 100
        : 0;
    // Roster limits: prefer the catalog payload (catalog_info.max_*_roster);
    // fall back to the limit the composition itself carries (the serializer's
    // `max`). Both are backend-driven — neither is ever a hardcoded number.
    const limitFor = (position, catalogLimit) =>
        (catalogLimit !== undefined && catalogLimit !== null)
            ? catalogLimit
            : getPositionLimit(composition, position);

    const maxQB = limitFor('qb', catalog?.max_qb_roster);
    const maxRB = limitFor('rb', catalog?.max_rb_roster);
    const maxWR = limitFor('wr', catalog?.max_wr_roster);
    const maxGear = limitFor('gear', catalog?.max_gear_roster);
    const qbCurrent = getPositionCount(composition, 'qb');
    const rbCurrent = getPositionCount(composition, 'rb');
    const wrCurrent = getPositionCount(composition, 'wr');
    const gearCurrent = getPositionCount(composition, 'gear');
    // Format "current/max", omitting the "/max" when the catalog doesn't
    // provide a limit (rather than displaying a wrong hardcoded number).
    // A null `current` means a contract violation was already logged — show a
    // dash so a broken payload never reads as a real count.
    const formatCount = (current, max) => {
        const currentLabel = current === null ? '—' : current;
        return (max !== undefined && max !== null) ? `${currentLabel}/${max}` : `${currentLabel}`;
    };

    // Status classes must not invent a limit: with an unknown count or limit we
    // can't say whether a position is complete, so report 'unknown'.
    const getPositionStatus = (position, current, target) => {
        if (typeof current !== 'number') return 'unknown';
        if (typeof target !== 'number') return current > 0 ? 'progress' : 'needed';
        if (current >= target) return 'complete';
        if (current > 0) return 'progress';
        return 'needed';
    };

    // Shared status text for a position card. '—' whenever we don't have both
    // numbers, rather than a confident-looking but wrong label.
    const getPositionStatusLabel = (current, max) => {
        if (typeof current !== 'number') return '—';
        if (current === 0) return 'NEEDED';
        if (typeof max !== 'number') return 'PARTIAL';
        return current < max ? 'PARTIAL' : 'COMPLETE';
    };

    const renderCompactView = () => (
        <div className="draft-progress-compact">
            <div className="progress-bar-container">
                <div className="progress-label">
                    Draft Progress: {formatCount(packsCompleted, totalPacks)} packs
                </div>
                <div className="progress-bar">
                    <div 
                        className="progress-fill" 
                        style={{ width: `${progressPercent}%` }}
                    />
                </div>
            </div>
            
            <div className="composition-compact">
                <span className={`position-badge qb ${getPositionStatus('qb', qbCurrent, maxQB)}`}>
                    QB {formatCount(qbCurrent, maxQB)}
                </span>
                <span className={`position-badge rb ${getPositionStatus('rb', rbCurrent, maxRB)}`}>
                    RB {formatCount(rbCurrent, maxRB)}
                </span>
                <span className={`position-badge wr ${getPositionStatus('wr', wrCurrent, maxWR)}`}>
                    WR {formatCount(wrCurrent, maxWR)}
                </span>
                <span className={`position-badge gear ${getPositionStatus('gear', gearCurrent, maxGear)}`}>
                    GEAR {formatCount(gearCurrent, maxGear)}
                </span>
                {teamState.is_draft_complete && (
                    <span className="complete-badge">✅ COMPLETE</span>
                )}
            </div>
        </div>
    );

    const renderFullView = () => (
        <div className="draft-progress-tracker">
            <div className="progress-header">
                <h3>Draft Progress</h3>
                <div className="draft-status">
                    {teamState.is_draft_complete ?
                        <span className="status-complete">Draft Complete!</span> :
                        <span className="status-active">Packs Completed: {formatCount(packsCompleted, totalPacks)}</span>
                    }
                </div>
            </div>

            {/* Progress Bar */}
            <div className="progress-section">
                <div className="progress-bar-full">
                    <div 
                        className="progress-fill-full" 
                        style={{ width: `${progressPercent}%` }}
                    />
                    <div className="progress-text">
                        {Math.round(progressPercent)}% Complete
                    </div>
                </div>
            </div>

            {/* Team Composition */}
            <div className="composition-section">
                <h4>Team Composition</h4>
                <div className="position-grid">
                    <div className={`position-card qb ${getPositionStatus('qb', qbCurrent, maxQB)}`}>
                        <div className="position-icon">🏈</div>
                        <div className="position-label">Quarterback</div>
                        <div className="position-count">
                            {formatCount(qbCurrent, maxQB)}
                        </div>
                        <div className="position-status">
                            {getPositionStatusLabel(qbCurrent, maxQB)}
                        </div>
                    </div>

                    <div className={`position-card rb ${getPositionStatus('rb', rbCurrent, maxRB)}`}>
                        <div className="position-icon">🏃</div>
                        <div className="position-label">Running Back</div>
                        <div className="position-count">
                            {formatCount(rbCurrent, maxRB)}
                        </div>
                        <div className="position-status">
                            {getPositionStatusLabel(rbCurrent, maxRB)}
                        </div>
                    </div>

                    <div className={`position-card wr ${getPositionStatus('wr', wrCurrent, maxWR)}`}>
                        <div className="position-icon">🎯</div>
                        <div className="position-label">Wide Receiver</div>
                        <div className="position-count">
                            {formatCount(wrCurrent, maxWR)}
                        </div>
                        <div className="position-status">
                            {getPositionStatusLabel(wrCurrent, maxWR)}
                        </div>
                    </div>

                    <div className={`position-card gear ${getPositionStatus('gear', gearCurrent, maxGear)}`}>
                        <div className="position-icon">⚙️</div>
                        <div className="position-label">Equipment</div>
                        <div className="position-count">
                            {formatCount(gearCurrent, maxGear)}
                        </div>
                        <div className="position-status">
                            {getPositionStatusLabel(gearCurrent, maxGear)}
                        </div>
                    </div>
                </div>
            </div>

            {/* Upcoming Packs Preview */}
            {availablePacks && availablePacks.length > 0 && !teamState.is_draft_complete && (
                <div className="upcoming-section">
                    <h4>Next Available</h4>
                    <div className="upcoming-packs">
                        {availablePacks.slice(0, 3).map((pack, index) => (
                            <div key={pack.id || index} className="upcoming-pack">
                                <div className="upcoming-pack-name">
                                    {pack.display_name || pack.name}
                                </div>
                                <div className="upcoming-pack-rarity">
                                    {pack.rarity_tier || 'COMMON'}
                                </div>
                            </div>
                        ))}
                        {availablePacks.length > 3 && (
                            <div className="upcoming-pack more">
                                +{availablePacks.length - 3} more
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Pack Selection Mode Info */}
            {catalog && (
                <div className="catalog-info">
                    <div className="catalog-mode">
                        Mode: {catalog.pack_selection_mode || 'SEQUENTIAL'}
                    </div>
                    {catalog.name && (
                        <div className="catalog-name">
                            Catalog: {catalog.name}
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    return compact ? renderCompactView() : renderFullView();
};

export default DraftProgressTracker;